/** Admin trust and money: safety reports, held messages, payments, deposits, freelancer payouts. */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { ago, fmtDate, fmtDateTime, inr } from '../../core/format.js';
import { reload, update } from '../../app/shell.js';
import { banner, empty, pageHead, seg, statusTag, table, tag } from '../../ui/components.js';
import { field, selectField } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { PAGE, filterSelect, pager, queryOf } from './shared.js';

/* ---------- safety ---------- */

const REASON = { spam: 'Spam', harassment: 'Harassment', fake_profile: 'Fake profile', scam: 'Scam', inappropriate: 'Inappropriate', other: 'Other', auto_hold: 'Held by the filter' };
const RESOLUTIONS = [['dismissed', 'Dismiss, no action'], ['warned', 'Warn the user'], ['messaging_restricted', 'Pause their messaging'], ['suspended', 'Suspend the account']];

const safety = {
  path: '/admin/safety',
  roles: ['admin'],
  title: 'Safety',
  ui: () => ({ tab: 'reports' }),
  async load(ctx) {
    const [r, h] = await Promise.all([api.get('/admin/reports', { signal: ctx.signal, query: { status: 'open' } }), api.get('/admin/held-messages', { signal: ctx.signal })]);
    return { reports: r.reports, held: h.messages };
  },
  view(d, ui) {
    return html`${pageHead('Safety', 'Reports from users and messages held by the filter. Nothing here is visible to the people involved until you act.')}
      <div class="mb">${seg([['reports', `Reports (${d.reports.length})`], ['held', `Held messages (${d.held.length})`]], ui.tab, 'safety.tab', 'Sections')}</div>
      ${ui.tab === 'reports'
        ? (d.reports.length ? html`<div class="list">${d.reports.map((r) => html`<div class="card mb"><div class="row between"><div><b>${REASON[r.reason] || r.reason}</b> <span class="mu sm">reported by ${r.reporter_name}, ${ago(r.created_at)}</span></div>${r.reported_name ? html`<span>About <b>${r.reported_name}</b></span>` : ''}</div>
          ${r.details ? html`<p class="sm mt-s" style="white-space:pre-line">${r.details}</p>` : ''}
          ${r.evidence.length ? html`<div class="mt-s"><div class="xs mu">Evidence</div>${r.evidence.map((e) => html`<blockquote class="sm" style="margin:4px 0;padding-left:10px;border-left:2px solid var(--ln)">${e.body || ''}</blockquote>`)}</div>` : ''}
          <button type="button" class="btn sm mt-s" data-act="report.resolve" data-id="${r.id}" data-name="${r.reported_name || 'this report'}">Resolve</button></div>`)}</div>`
          : empty('shield', 'No open reports', 'Nothing needs a decision.'))
        : (d.held.length ? table(['From', 'Conversation', 'Message', 'Why held', 'When', ''], d.held.map((m) => [html`<b>${m.sender}</b>`, m.conversation, html`<span style="white-space:pre-line">${m.body}</span>`, m.hold_reason, ago(m.at),
          html`<div class="row nw"><button type="button" class="btn sm" data-act="held.decide" data-id="${m.id}" data-action="approve">Allow</button><button type="button" class="btn danger sm" data-act="held.decide" data-id="${m.id}" data-action="remove">Remove</button></div>`]))
          : empty('check', 'No held messages', 'The filter has nothing waiting for you.'))}`;
  },
};

register({
  'safety.tab': (el) => update((ui) => { ui.tab = el.dataset.value; }),
  'report.resolve': (el) => openModal({
    title: `Resolve report about ${el.dataset.name}`,
    body: html`<form id="res-form" data-submit="report.resolve.send" data-id="${el.dataset.id}" novalidate>${selectField('rs-res', 'Decision', RESOLUTIONS, { name: 'resolution', placeholder: 'Choose' })}${field('rs-note', 'Staff note (kept on the report)', { name: 'note', req: false, rows: 3, max: 500 })}</form>
      <p class="note">Warnings and pauses notify the user. Suspension signs them out everywhere.</p>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="res-form" class="btn">Resolve</button>`,
  }),
  'report.resolve.send': (form) => submitForm(form, async ({ resolution, note }) => {
    await api.post(`/admin/reports/${form.dataset.id}/resolve`, { body: { resolution, note } });
    closeModal(false);
    toast('Report resolved', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
  'held.decide': async (el) => {
    await api.post(`/admin/held-messages/${el.dataset.id}/decide`, { body: { action: el.dataset.action } });
    toast(el.dataset.action === 'approve' ? 'Message allowed' : 'Message removed', 'check');
    await reload();
  },
});

/* ---------- payments ---------- */

const payments = {
  path: '/admin/payments',
  roles: ['admin'],
  title: 'Payments',
  async load(ctx) {
    const query = queryOf(ctx);
    const { payments: list } = await api.get('/admin/payments', { signal: ctx.signal, query: { ...query, limit: PAGE } });
    return { list, query };
  },
  view({ list, query }) {
    return html`${pageHead('Payments', 'Every order, paid or not. Card and UPI details never reach VConnect. For transfers you receive by hand, check the bank first, then Confirm.')}
      <div class="row mb">${filterSelect('Status', 'status', [['paid', 'Paid'], ['created', 'Awaiting payment'], ['failed', 'Failed']], query.status)}${filterSelect('Type', 'kind', [['subscription', 'Subscription'], ['deposit', 'Deposit']], query.kind)}</div>
      ${list.length
        ? table(['Order', 'User', 'Course / batch', 'Type', 'Base', 'Discount', 'GST', 'Total', 'Reference', 'Status', 'Date', ''], list.map((o) => [`#${o.id}`, o.user_name, courseCell(o), o.kind, inr(o.base_paise), o.discount_paise ? `-${inr(o.discount_paise)}` : '', inr(o.gst_paise), html`<b>${inr(o.total_paise)}</b>`, html`<span class="mono">${o.payment_reference || ''}</span>`, statusTag(o.status), fmtDate(o.paid_at || o.created_at), manualActions(o)]))
        : empty('rupee', 'No payments', 'No orders match this filter.')}
      ${pager('/admin/payments', query, list.length)}`;
  },
};

function courseCell(o) {
  if (!o.course_title) return '';
  const full = o.batch_name && o.batch_seats_left === 0 && o.status === 'created';
  return html`${o.course_title}${o.batch_name ? html`<div class="sm mu">Batch ${o.batch_name}${full ? html` <span class="tag wn">Batch full</span>` : ''}</div>` : ''}`;
}

const isManual = (o) => String(o.gateway_order_id || '').startsWith('order_manual_') && o.status === 'created';

function manualActions(o) {
  if (!isManual(o)) return '';
  return html`<div class="row nw"><button type="button" class="btn sm" data-act="pay.confirm" data-id="${o.id}" data-name="${o.user_name}" data-total="${o.total_paise}" data-ref="${o.payment_reference || ''}" data-full="${o.batch_name && o.batch_seats_left === 0 ? '1' : ''}" ${o.payment_reference ? '' : 'title="No reference yet: check your bank statement"'}>Confirm</button>
    <button type="button" class="btn ghost sm" data-act="pay.reject" data-id="${o.id}" data-name="${o.user_name}">Reject</button></div>`;
}

register({
  'pay.confirm': async (el) => {
    const ok = await confirmBox({ title: `Confirm order #${el.dataset.id}?`, text: `${el.dataset.name} should have paid ${inr(Number(el.dataset.total))}${el.dataset.ref ? ` (reference ${el.dataset.ref})` : ''}. Only confirm after you see the money in the bank: this gives access and issues an invoice.${el.dataset.full ? ' The batch is already full, so this student will have access but no batch until you place them.' : ''}`, label: 'Money received, confirm' });
    if (!ok) return;
    const done = await api.post(`/admin/payments/${el.dataset.id}/decide`, { body: { action: 'confirm' } });
    toast(done.warning || 'Payment confirmed and invoice issued', done.warning ? 'alert' : 'check');
    await reload();
  },
  'pay.reject': (el) => openModal({
    title: `Reject order #${el.dataset.id}`,
    body: html`<form id="rej-form" data-submit="pay.reject.send" data-id="${el.dataset.id}" novalidate>${field('rj-reason', 'Reason (the student sees this)', { name: 'reason', minlen: 3, maxlength: 200, ph: 'Payment not found in the bank' })}</form>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="rej-form" class="btn danger">Reject</button>`,
  }),
  'pay.reject.send': (form) => submitForm(form, async ({ reason }) => {
    await api.post(`/admin/payments/${form.dataset.id}/decide`, { body: { action: 'reject', reason } });
    closeModal(false);
    toast('Order rejected', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- deposits ---------- */

const DEPOSIT_TAG = { Active: ['Active', 'ok'], Expired: ['Refund due', 'wn'], Flagged: ['Flagged', 'bad'], Closed: ['Closed', 'n'] };
const REFUND_TAG = { pending: ['Refund pending', 'n'], on_hold: ['On hold', 'bad'], kept: ['Kept', 'n'], refunded: ['Refunded', 'ok'] };

const deposits = {
  path: '/admin/deposits',
  roles: ['admin'],
  title: 'Deposits',
  async load(ctx) {
    const query = queryOf(ctx);
    const { deposits: list } = await api.get('/admin/deposits', { signal: ctx.signal, query: { limit: PAGE, offset: query.offset || 0 } });
    return { list, query };
  },
  view({ list, query }) {
    return html`${pageHead('Verification deposits', 'Refundable deposits held from freelancers. Refund after the hold unless you flag a violation.')}
      ${list.length
        ? table(['Freelancer', 'Amount', 'Status', 'Refund', 'Refund after', 'Note', ''], list.map((d) => {
          const [label, cls] = DEPOSIT_TAG[d.display_status] || [d.display_status, 'n'];
          const [rl, rc] = REFUND_TAG[d.refund_status] || [d.refund_status, 'n'];
          return [html`<b>${d.user_name}</b>`, inr(d.amount_paise), tag(label, cls), tag(rl, rc), fmtDate(d.valid_until), d.note || '', d.status === 'closed' ? '' : depositActions(d)];
        }))
        : empty('wallet', 'No deposits yet', 'Deposits appear when freelancers pay.')}
      ${pager('/admin/deposits', query, list.length)}`;
  },
};

function depositActions(d) {
  const b = (action, label, cls = 'ghost') => html`<button type="button" class="btn ${cls} sm" data-act="deposit.act" data-id="${d.id}" data-action="${action}" data-name="${d.user_name}">${label}</button>`;
  return html`<div class="row nw">${d.display_status === 'Expired' && d.status === 'active' ? html`<button type="button" class="btn sm" data-act="deposit.refund" data-id="${d.id}" data-name="${d.user_name}">Refund</button>` : ''}
    ${d.status === 'flagged' ? b('clear', 'Clear flag') : b('flag', 'Flag')}${b('extend', 'Extend')}${b('keep', 'Keep deposit', 'danger')}</div>`;
}

const DEPOSIT_TEXT = {
  flag: ['Flag this deposit', 'The refund is held until you clear the flag.'],
  clear: ['Clear the flag', 'The refund goes back to the normal schedule.'],
  extend: ['Extend the hold', 'Pushes the refund date later.'],
  keep: ['Keep this deposit', 'The deposit is forfeited and closed. Use only for confirmed policy violations.'],
};

register({
  'deposit.act': (el) => {
    const action = el.dataset.action;
    const [title, note] = DEPOSIT_TEXT[action];
    openModal({
      title: `${title}: ${el.dataset.name}`,
      body: html`<form id="dep-form" data-submit="deposit.act.send" data-id="${el.dataset.id}" data-action="${action}" novalidate>
        ${action === 'extend' ? field('dp-days', 'Extend by (days)', { name: 'extend_days', type: 'number', min: 1, max: 90, val: 7 }) : ''}${field('dp-note', 'Note', { name: 'note', req: action === 'keep' || action === 'flag', maxlength: 300, minlen: 3 })}</form><p class="note">${note}</p>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="dep-form" class="btn ${action === 'keep' ? 'danger' : ''}">${title}</button>`,
    });
  },
  'deposit.act.send': (form) => submitForm(form, async ({ note, extend_days: days }) => {
    const body = { action: form.dataset.action, note };
    if (form.dataset.action === 'extend') body.extend_days = Number(days);
    await api.post(`/admin/deposits/${form.dataset.id}/action`, { body });
    closeModal(false);
    toast('Deposit updated', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
  'deposit.refund': async (el) => {
    if (!(await confirmBox({ title: `Refund ${el.dataset.name}'s deposit?`, text: 'The money goes back to the original payment method through the gateway. This cannot be undone.', label: 'Refund now' }))) return;
    await api.post(`/admin/deposits/${el.dataset.id}/refund`);
    toast('Refund started', 'check');
    await reload();
  },
});

/* ---------- payouts ---------- */

const payouts = {
  path: '/admin/payouts',
  roles: ['admin'],
  title: 'Payouts',
  load: (ctx) => api.get('/admin/withdrawals', { signal: ctx.signal, query: { status: ctx.query.get('status') || 'requested' } }),
  view(d, _ui, ctx) {
    const status = ctx.query.get('status') || 'requested';
    return html`${pageHead('Freelancer payouts', 'Pay each request from your bank or UPI, then mark it paid here so the freelancer is told.')}
      ${banner('VConnect does not move money for you. Mark a request paid only after the transfer has gone through.', 'wn', 'alert')}
      <div class="row mt mb">${filterSelect('Status', 'status', [['paid', 'Paid'], ['rejected', 'Rejected']], status === 'requested' ? '' : status, 'Requested')}</div>
      ${d.withdrawals.length
        ? table(['Freelancer', 'Amount', 'Send to', 'Requested', 'Status', ''], d.withdrawals.map((w) => [html`<b>${w.user_name}</b>`, inr(w.amount_paise), w.destination_label, fmtDateTime(w.requested_at), statusTag(w.status),
          w.status === 'requested' ? html`<div class="row nw"><button type="button" class="btn sm" data-act="payout.process" data-id="${w.id}" data-action="paid" data-name="${w.user_name}" data-amount="${w.amount_paise}">Mark paid</button>
            <button type="button" class="btn danger sm" data-act="payout.process" data-id="${w.id}" data-action="rejected" data-name="${w.user_name}" data-amount="${w.amount_paise}">Reject</button></div>` : (w.note || '')]))
        : empty('wallet', 'No requests here', 'Withdrawal requests from freelancers appear here.')}`;
  },
};

register({
  'payout.process': (el) => {
    const paid = el.dataset.action === 'paid';
    openModal({
      title: `${paid ? 'Mark paid' : 'Reject'}: ${inr(el.dataset.amount)} to ${el.dataset.name}`,
      body: html`<form id="pay-form" data-submit="payout.process.send" data-id="${el.dataset.id}" data-action="${el.dataset.action}" novalidate>${field('po-note', paid ? 'Transfer reference' : 'Reason (the freelancer is told)', { name: 'note', req: !paid, maxlength: 300, minlen: 3, ph: paid ? 'UTR or UPI reference' : '' })}</form>
        <p class="note">${paid ? 'Only mark paid after the transfer is done.' : 'The amount goes back to their available balance.'}</p>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="pay-form" class="btn ${paid ? '' : 'danger'}">${paid ? 'Mark paid' : 'Reject request'}</button>`,
    });
  },
  'payout.process.send': (form) => submitForm(form, async ({ note }) => {
    await api.post(`/admin/withdrawals/${form.dataset.id}/process`, { body: { action: form.dataset.action, note } });
    closeModal(false);
    toast('Payout updated', 'check');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

export const routes = [safety, payments, deposits, payouts];
